import React, { useState, useEffect, useRef, useCallback } from 'react';
import './IVIATimer.scss'; // Create this file for styling
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faUser,
  faBook,
  faSun,
  faMoon,
  faBell,
  faBellSlash,
  faCalendarAlt,
  // faPlay,
  faComments,
  faPlus,
  faMinus,
  faTextHeight,
  faEye,
  faEyeSlash,
  faSearchPlus,
  faSearchMinus,
  faExpand,
  faCompress,
  faSync,
  faBars,
  faTimes,
  faCog,
  faExchangeAlt,
} from '@fortawesome/free-solid-svg-icons';
import scheduleData from '../../data/schedule.json';
import ScheduleModal from '../ScheduleModal/ScheduleModal';
import DataSourceSelector from '../DataSourceSelector/DataSourceSelector';
import { fetchAndParseICS, parseLocalICSFile, validateScheduleData, parseICSContent } from '../../utils/icsParser';
import { labRetreatIcsData } from '../../utils/sampleIcsData';
import useRealtimeSync from '../../hooks/useRealtimeSync';
// import { testLabRetreatICS, displayParsedData } from '../../utils/testIcsParser'; // Uncomment for testing

const IVIATimer = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [lastActiveSchedule] = useState(null);
  const [nextSchedule, setNextSchedule] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('iviaTimer.isDarkMode');
    return stored ? JSON.parse(stored) : true; // Default to dark mode
  });
  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('iviaTimer.isSoundEnabled');
    return stored ? JSON.parse(stored) : true; // Default to sound enabled
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const audioRef = useRef(new Audio('/sounds/timer-bell-nz-alarm.mp3')); // Make sure to add this sound file
  const [isDiscussionTime, setIsDiscussionTime] = useState(false);
  // Real-time sync for offset
  const initialOffset = (() => {
    const stored = localStorage.getItem('iviaTimer.scheduleOffset');
    return stored ? parseInt(stored, 10) : 0;
  })();

  const {
    offset: scheduleOffset,
    updateOffset: setScheduleOffset,
    connectedClients,
    isConnected: isRealtimeSyncConnected,
  } = useRealtimeSync(initialOffset);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [textSizeMultiplier, setTextSizeMultiplier] = useState(() => {
    const stored = localStorage.getItem('iviaTimer.textSize');
    return stored ? parseFloat(stored) : 1; // Default to normal size
  });
  const [showMiniSchedule, setShowMiniSchedule] = useState(() => {
    const stored = localStorage.getItem('iviaTimer.showMiniSchedule');
    return stored ? JSON.parse(stored) : true; // Default to shown
  });
  const [timeUntilNextSession, setTimeUntilNextSession] = useState(null);

  // Data source management
  const [dataSource, setDataSource] = useState(() => {
    const stored = localStorage.getItem('iviaTimer.dataSource');
    return stored || 'json'; // Default to JSON schedule data
  });
  const [icsUrl, setIcsUrl] = useState(() => {
    const stored = localStorage.getItem('iviaTimer.icsUrl');
    return stored || 'https://php-proxy.ivia.ch/ivia-timer/index.php';
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [lastDataUpdate, setLastDataUpdate] = useState(null);
  const [showDataSelector, setShowDataSelector] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showOffsetEditor, setShowOffsetEditor] = useState(false);
  const [offsetInputValue, setOffsetInputValue] = useState('');

  // UI auto-hide (presentation mode)
  const [isUIHidden, setIsUIHidden] = useState(false);
  const autoHideTimerRef = useRef(null);
  const AUTO_HIDE_DELAY = 5 * 60 * 1000; // 5 minutes

  // Transition time feature
  const [transitionTime, setTransitionTime] = useState(() => {
    const stored = localStorage.getItem('iviaTimer.transitionTime');
    return stored ? parseInt(stored, 10) : 0;
  });
  const [isTransitionTime, setIsTransitionTime] = useState(false);

  // Add these effects to save settings when they change
  useEffect(() => {
    localStorage.setItem('iviaTimer.isDarkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('iviaTimer.isSoundEnabled', JSON.stringify(isSoundEnabled));
  }, [isSoundEnabled]);

  // Save offset to localStorage when it changes (both local and remote updates)
  useEffect(() => {
    localStorage.setItem('iviaTimer.scheduleOffset', scheduleOffset.toString());
  }, [scheduleOffset]);

  useEffect(() => {
    localStorage.setItem('iviaTimer.textSize', textSizeMultiplier.toString());
    document.documentElement.style.setProperty('--text-size-multiplier', textSizeMultiplier);
  }, [textSizeMultiplier]);

  useEffect(() => {
    localStorage.setItem('iviaTimer.showMiniSchedule', JSON.stringify(showMiniSchedule));
  }, [showMiniSchedule]);

  useEffect(() => {
    localStorage.setItem('iviaTimer.dataSource', dataSource);
  }, [dataSource]);

  useEffect(() => {
    localStorage.setItem('iviaTimer.icsUrl', icsUrl);
  }, [icsUrl]);

  useEffect(() => {
    localStorage.setItem('iviaTimer.transitionTime', transitionTime.toString());
  }, [transitionTime]);

  // Auto-hide UI after 5 minutes of inactivity
  const resetAutoHideTimer = useCallback(() => {
    if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    autoHideTimerRef.current = setTimeout(() => {
      setIsUIHidden(true);
    }, AUTO_HIDE_DELAY);
  }, [AUTO_HIDE_DELAY]);

  useEffect(() => {
    resetAutoHideTimer();
    const handleActivity = () => {
      if (isUIHidden) setIsUIHidden(false);
      resetAutoHideTimer();
    };
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('touchstart', handleActivity);
    return () => {
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('touchstart', handleActivity);
    };
  }, [isUIHidden, resetAutoHideTimer]);

  const toggleUIVisibility = () => {
    setIsUIHidden((prev) => {
      if (prev) {
        resetAutoHideTimer();
        return false;
      }
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      return true;
    });
  };

  // Data loading functions
  const loadJSONData = useCallback(() => {
    try {
      setIsLoadingData(true);
      setDataError(null);

      if (validateScheduleData(scheduleData)) {
        setSchedule(scheduleData.schedule);
        setLastDataUpdate(new Date());
      } else {
        throw new Error('Invalid JSON schedule data format');
      }
    } catch (error) {
      setDataError('Failed to load JSON data: ' + error.message);
      console.error('Error loading JSON data:', error);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  const loadICSData = useCallback(async (url) => {
    try {
      setIsLoadingData(true);
      setDataError(null);

      const icsData = await fetchAndParseICS(url);

      if (validateScheduleData(icsData)) {
        setSchedule(icsData.schedule);
        setLastDataUpdate(new Date());
        setIcsUrl(url);
      } else {
        throw new Error('Invalid ICS data format');
      }
    } catch (error) {
      setDataError('Failed to load ICS data: ' + error.message);
      console.error('Error loading ICS data:', error);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  const loadFileData = useCallback(async (file) => {
    try {
      setIsLoadingData(true);
      setDataError(null);

      const icsData = await parseLocalICSFile(file);

      if (validateScheduleData(icsData)) {
        setSchedule(icsData.schedule);
        setLastDataUpdate(new Date());
      } else {
        throw new Error('Invalid ICS file format');
      }
    } catch (error) {
      setDataError('Failed to load ICS file: ' + error.message);
      console.error('Error loading ICS file:', error);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  const loadSampleData = useCallback(() => {
    try {
      setIsLoadingData(true);
      setDataError(null);

      const icsData = parseICSContent(labRetreatIcsData);

      if (validateScheduleData(icsData)) {
        setSchedule(icsData.schedule);
        setLastDataUpdate(new Date());
        console.log('Loaded sample lab retreat data with', icsData.schedule.length, 'events');
      } else {
        throw new Error('Invalid sample ICS data format');
      }
    } catch (error) {
      setDataError('Failed to load sample data: ' + error.message);
      console.error('Error loading sample data:', error);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  const handleDataSourceChange = useCallback(
    (sourceType, sourceData) => {
      setDataSource(sourceType);

      switch (sourceType) {
        case 'json':
          loadJSONData();
          break;
        case 'ics':
          if (sourceData) {
            loadICSData(sourceData);
          }
          break;
        case 'file':
          if (sourceData) {
            loadFileData(sourceData);
          }
          break;
        case 'sample':
          loadSampleData();
          break;
        default:
          break;
      }
    },
    [loadJSONData, loadICSData, loadFileData, loadSampleData],
  );

  const refreshCurrentData = useCallback(() => {
    if (dataSource === 'ics' && icsUrl) {
      loadICSData(icsUrl);
    } else if (dataSource === 'json') {
      loadJSONData();
    }
  }, [dataSource, icsUrl, loadICSData, loadJSONData]);

  // Wrap playSound in useCallback
  const playSound = useCallback(() => {
    if (isSoundEnabled && !isPlaying) {
      setIsPlaying(true);
      audioRef.current.play().catch((error) => {
        console.error('Error playing sound:', error);
      });
    }
  }, [isSoundEnabled, isPlaying]);

  // Initial data loading
  useEffect(() => {
    if (dataSource === 'ics' && icsUrl) {
      loadICSData(icsUrl);
    } else {
      loadJSONData();
    }
  }, [dataSource, icsUrl, loadICSData, loadJSONData]); // Only run when these dependencies change

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play().catch((error) => {
        console.error('Error playing sound:', error);
      });

      const stopTimer = setTimeout(() => {
        setIsPlaying(false);
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }, 10000);

      return () => clearTimeout(stopTimer);
    }
  }, [isPlaying]);

  const toggleSound = () => {
    try {
      // First stop any currently playing sound
      if (isPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
      }
      // Then toggle the sound enabled state
      setIsSoundEnabled((prev) => !prev);
    } catch (error) {
      console.error('Error toggling sound:', error);
      // Fallback in case of localStorage errors
      setIsSoundEnabled(true);
    }
  };

  // Helper function to adjust schedule times with offset
  const getAdjustedTime = useCallback(
    (originalTime, date) => {
      // const [hours, minutes] = originalTime.split(':');
      const time = new Date(`${date}T${originalTime}:00`);
      time.setMinutes(time.getMinutes() + scheduleOffset);
      return time;
    },
    [scheduleOffset],
  );

  // Calculate phase time with optional transition time support
  const calculatePhaseTime = useCallback(
    (currentTime, schedule) => {
      if (!schedule) return null;

      const startTime = getAdjustedTime(schedule.time, schedule.date);
      const presentationDuration = schedule.duration - (schedule.discussion || 0);
      const presentationEndTime = new Date(startTime.getTime() + presentationDuration * 60000);
      const totalEndTime = new Date(startTime.getTime() + schedule.duration * 60000);

      const transitionMs = transitionTime > 0 ? transitionTime * 60000 : 0;
      const hasDiscussion = schedule.discussion && schedule.discussion > 0;

      if (transitionMs > 0) {
        // Split transition 50/50 between presentation end and discussion end
        const presTransMs = hasDiscussion ? transitionMs / 2 : transitionMs;
        const discTransMs = hasDiscussion ? transitionMs / 2 : 0;

        // Cap transition so it doesn't exceed the phase duration
        const maxPresTransMs = Math.min(presTransMs, presentationDuration * 60000 * 0.5);
        const maxDiscTransMs = hasDiscussion ? Math.min(discTransMs, schedule.discussion * 60000 * 0.5) : 0;

        const presContentEnd = new Date(presentationEndTime.getTime() - maxPresTransMs);
        const discContentEnd = hasDiscussion ? new Date(totalEndTime.getTime() - maxDiscTransMs) : null;

        if (hasDiscussion) {
          // Discussion transition phase (last N/2 seconds of discussion)
          if (currentTime >= discContentEnd && currentTime < totalEndTime) {
            return { isDiscussion: false, isTransition: true, remainingTime: totalEndTime - currentTime };
          }
          // Discussion content phase
          if (currentTime >= presentationEndTime && currentTime < discContentEnd) {
            return { isDiscussion: true, isTransition: false, remainingTime: discContentEnd - currentTime };
          }
          // Presentation transition phase (last N/2 seconds of presentation)
          if (currentTime >= presContentEnd && currentTime < presentationEndTime) {
            return { isDiscussion: false, isTransition: true, remainingTime: presentationEndTime - currentTime };
          }
          // Presentation content phase
          if (currentTime >= startTime && currentTime < presContentEnd) {
            return { isDiscussion: false, isTransition: false, remainingTime: presContentEnd - currentTime };
          }
        } else {
          // No discussion: transition at end of presentation
          if (currentTime >= presContentEnd && currentTime < totalEndTime) {
            return { isDiscussion: false, isTransition: true, remainingTime: totalEndTime - currentTime };
          }
          if (currentTime >= startTime && currentTime < presContentEnd) {
            return { isDiscussion: false, isTransition: false, remainingTime: presContentEnd - currentTime };
          }
        }
      } else {
        // No transition time -- original behavior
        if (currentTime >= presentationEndTime && currentTime < totalEndTime && hasDiscussion) {
          return { isDiscussion: true, isTransition: false, remainingTime: totalEndTime - currentTime };
        }
        if (currentTime >= startTime && currentTime < presentationEndTime) {
          return { isDiscussion: false, isTransition: false, remainingTime: presentationEndTime - currentTime };
        }
      }

      return null;
    },
    [getAdjustedTime, transitionTime],
  );

  // Add this new function to get the last session
  const getLastSession = useCallback(
    (currentTime, schedule, currentIndex) => {
      if (!schedule || schedule.length === 0) return null;

      // If we have no current session (currentIndex === -1), find the last completed session
      if (currentIndex === -1) {
        // Find the last session that has ended before current time
        return schedule.reduce((lastCompleted, item) => {
          const startTime = getAdjustedTime(item.time, item.date);
          const endTime = new Date(startTime.getTime() + item.duration * 60000);

          if (
            currentTime > endTime &&
            (!lastCompleted || endTime > getAdjustedTime(lastCompleted.time, lastCompleted.date))
          ) {
            return item;
          }
          return lastCompleted;
        }, null);
      }

      // If we have a current session, get the previous session if it exists
      return currentIndex > 0 ? schedule[currentIndex - 1] : null;
    },
    [getAdjustedTime],
  );

  // Update the main useEffect that handles schedule updates
  useEffect(() => {
    const currentIndex = schedule.findIndex((item) => {
      const startTime = getAdjustedTime(item.time, item.date);
      const totalDuration = item.duration * 60000;
      const endTime = new Date(startTime.getTime() + totalDuration);
      return currentTime >= startTime && currentTime < endTime;
    });

    const current = schedule[currentIndex];

    // Find next schedule item
    let next = null;
    if (currentIndex !== -1) {
      // Only look for next item if we're not at the last item with a duration
      if (currentIndex < schedule.length - 1) {
        next = schedule.slice(currentIndex + 1).find((item) => {
          const itemDateTime = getAdjustedTime(item.time, item.date);
          return itemDateTime > currentTime && item.duration > 0;
        });
      }
    } else {
      next = schedule.find((item) => {
        const itemDateTime = getAdjustedTime(item.time, item.date);
        return itemDateTime > currentTime && item.duration > 0;
      });
    }

    setCurrentSchedule(current);
    setNextSchedule(next);

    if (current) {
      const phaseInfo = calculatePhaseTime(currentTime, current);

      // Check if current session is a break and show next presenter preview
      if (current.category === 'Break') {
        const startTime = getAdjustedTime(current.time, current.date);
        const endTime = new Date(startTime.getTime() + current.duration * 60000);
        const timeUntilEnd = endTime - currentTime;

        // If less than 10 minutes left in break and there's a next session
        if (timeUntilEnd <= 600000 && next) {
          setCountdown(timeUntilEnd);
          setNextSchedule(next);
        } else {
          // Show countdown even if there's no next session
          setCountdown(timeUntilEnd > 0 ? timeUntilEnd : null);
        }
      } else if (phaseInfo) {
        setIsDiscussionTime(phaseInfo.isDiscussion);
        setIsTransitionTime(phaseInfo.isTransition || false);
        setCountdown(phaseInfo.remainingTime);

        // Set timer for phase end
        const timer = setTimeout(() => {
          if (isSoundEnabled) {
            playSound();
          }
        }, phaseInfo.remainingTime);

        return () => clearTimeout(timer);
      } else {
        setCountdown(null);
        setIsDiscussionTime(false);
        setIsTransitionTime(false);
      }
    } else {
      setCountdown(null);
      setIsDiscussionTime(false);
      setIsTransitionTime(false);
    }

    // Show countdown for next talk during breaks
    if (!current && next) {
      const nextStartTime = getAdjustedTime(next.time, next.date);
      const timeUntilNext = nextStartTime - currentTime;
      setCountdown(timeUntilNext > 0 ? timeUntilNext : null);

      // Show next presenter if the next session is within 10 minutes
      if (timeUntilNext <= 600000) {
        setNextSchedule(next);
      }
    }
  }, [currentTime, schedule, isSoundEnabled, playSound, calculatePhaseTime, getAdjustedTime, getLastSession]);

  useEffect(() => {
    if (!currentSchedule && nextSchedule) {
      const nextStartTime = getAdjustedTime(nextSchedule.time, nextSchedule.date);
      const timeUntilStart = nextStartTime - currentTime;
      setTimeUntilNextSession(timeUntilStart > 0 ? timeUntilStart : null);
    } else {
      setTimeUntilNextSession(null);
    }
  }, [currentTime, currentSchedule, nextSchedule, getAdjustedTime]);

  const getCountdownColor = (milliseconds) => {
    if (!currentSchedule || !countdown) return '';

    if (isTransitionTime) {
      if (milliseconds <= 15000) return 'critical-time transition';
      if (milliseconds <= 30000) return 'warning-time transition';
      return 'transition-time';
    } else if (isDiscussionTime) {
      if (milliseconds <= 30000) return 'critical-time discussion';
      if (milliseconds <= 60000) return 'warning-time discussion';
      return 'discussion-time';
    } else {
      if (milliseconds <= 60000) return 'critical-time';
      if (milliseconds <= 120000) return 'warning-time';
      return '';
    }
  };

  const getPhaseLabel = () => {
    if (isTransitionTime) return 'Transition Time';
    if (!currentSchedule?.discussion && !isTransitionTime) return '';
    return isDiscussionTime ? 'Discussion Time' : 'Presentation Time';
  };

  const formatCountdown = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const getSurroundingPresentations = () => {
    const activeSchedule = currentSchedule || lastActiveSchedule;
    if (!activeSchedule) return [];

    const currentIndex = schedule.findIndex((item) => item === activeSchedule);
    const start = Math.max(0, currentIndex - 2);
    const end = Math.min(schedule.length, currentIndex + 3);

    return schedule.slice(start, end).map((item) => ({
      ...item,
      isCurrent: item === currentSchedule,
      isNext: item === nextSchedule,
    }));
  };

  const toggleDarkMode = () => {
    try {
      setIsDarkMode((prev) => !prev);
    } catch (error) {
      console.error('Error toggling dark mode:', error);
      // Fallback in case of localStorage errors
      setIsDarkMode(true);
    }
  };

  const toggleScheduleModal = () => {
    setShowScheduleModal(!showScheduleModal);
  };

  // Helper function to get session type icons
  const getSessionIcons = (scheduleItem) => {
    switch (scheduleItem.category) {
      case 'PhD Updates':
        return { presenterIcon: faUser, categoryIcon: faBook };
      case 'Break':
        return { presenterIcon: '☕', categoryIcon: '🍵' }; // Coffee icon for breaks, tea icon for category
      case 'Lunch':
        return { presenterIcon: '🍽️', categoryIcon: '🍔' }; // Plate and cutlery icon for lunch, burger icon for category
      case 'Feedback Sessions':
        return { presenterIcon: faComments, categoryIcon: faComments };
      case 'Discussion':
        return { presenterIcon: faComments, categoryIcon: faComments };
      case 'Session Start':
      case 'Session End':
        return { presenterIcon: faCalendarAlt, categoryIcon: faCalendarAlt };
      default:
        return { presenterIcon: faUser, categoryIcon: faBook };
    }
  };

  // Update the adjustOffset function to include error handling
  const adjustOffset = (amount) => {
    try {
      const currentTime = new Date().getTime();
      const timeDiff = currentTime - lastClickTime;
      setLastClickTime(currentTime);

      let newOffset;
      if (timeDiff < 300) {
        newOffset = scheduleOffset + amount * 5;
      } else {
        newOffset = scheduleOffset + amount;
      }

      // Ensure the new offset is a valid number
      if (typeof newOffset === 'number' && !isNaN(newOffset)) {
        setScheduleOffset(newOffset);
      }
    } catch (error) {
      console.error('Error adjusting offset:', error);
      // Fallback in case of localStorage errors
      setScheduleOffset(0);
    }
  };

  // Add this function near your other handlers
  const stopSound = () => {
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const toggleTextSize = () => {
    try {
      setTextSizeMultiplier((prev) => {
        // Cycle through sizes: 1 -> 1.2 -> 1.4 -> 1
        if (prev >= 1.4) return 1;
        return prev + 0.2;
      });
    } catch (error) {
      console.error('Error toggling text size:', error);
      setTextSizeMultiplier(1);
    }
  };

  const toggleMiniSchedule = () => {
    setShowMiniSchedule((prev) => !prev);
  };

  const cycleTransitionTime = () => {
    setTransitionTime((prev) => {
      if (prev >= 3) return 0;
      return prev + 1;
    });
  };

  // Add this helper function near your other formatting functions
  const formatDuration = (minutes, showMinutes = true) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}:${mins.toString().padStart(2, '0')}` : `${mins}${showMinutes ? ' min' : ''}`;
  };

  // Add this helper function with your other formatting functions
  const formatOffset = (minutes) => {
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    const sign = minutes >= 0 ? '+' : '-';
    return hours > 0 ? `${sign}${hours}:${mins.toString().padStart(2, '0')}` : `${sign}${mins}`;
  };

  // Update the zoom functions to apply scaling to all text
  const zoomIn = () => {
    setTextSizeMultiplier((prev) => {
      const newValue = Math.min(prev + 0.2, 2); // Limit max size to 2
      document.documentElement.style.setProperty('--text-size-multiplier', newValue);
      return newValue;
    });
  };

  const zoomOut = () => {
    setTextSizeMultiplier((prev) => {
      const newValue = Math.max(prev - 0.2, 0.6); // Limit min size to 0.6
      document.documentElement.style.setProperty('--text-size-multiplier', newValue);
      return newValue;
    });
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--text-size-multiplier', textSizeMultiplier);
  }, [textSizeMultiplier]);

  // Function to toggle full-screen mode
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const formatTimeUntilNextSession = (milliseconds) => {
    if (milliseconds <= 0) return 'Starting soon';
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const timeString =
      hours > 0
        ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        : `${minutes}:${seconds.toString().padStart(2, '0')}`;
    return `Next session starts in ${timeString}`;
  };

  const resetOffset = () => {
    try {
      setScheduleOffset(0); // This will broadcast to all clients
    } catch (error) {
      console.error('Error resetting offset:', error);
      setScheduleOffset(0);
    }
  };

  const handleOffsetRightClick = (e) => {
    e.preventDefault();
    setOffsetInputValue(scheduleOffset.toString());
    setShowOffsetEditor(true);
  };

  const handleOffsetSubmit = () => {
    const newOffset = parseInt(offsetInputValue, 10);
    if (!isNaN(newOffset)) {
      setScheduleOffset(newOffset); // This will broadcast to all clients
    }
    setShowOffsetEditor(false);
  };

  const handleOffsetCancel = () => {
    setShowOffsetEditor(false);
    setOffsetInputValue('');
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  // Add useEffect for ESC key handling and outside clicks
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showScheduleModal) {
          setShowScheduleModal(false);
        }
        if (showMobileMenu) {
          setShowMobileMenu(false);
        }
        if (showDataSelector) {
          setShowDataSelector(false);
        }
        if (showOffsetEditor) {
          setShowOffsetEditor(false);
        }
      }
    };

    const handleClickOutside = (event) => {
      // Close mobile menu when clicking outside
      if (showMobileMenu && !event.target.closest('.controls-section')) {
        setShowMobileMenu(false);
      }

      // Close data selector when clicking outside
      if (showDataSelector && !event.target.closest('.data-source-overlay') && !event.target.closest('.control-btn')) {
        setShowDataSelector(false);
      }

      // Close offset editor when clicking outside
      if (showOffsetEditor && !event.target.closest('.offset-editor-modal')) {
        setShowOffsetEditor(false);
      }
    };

    // Add event listeners
    document.addEventListener('keydown', handleEscKey);
    document.addEventListener('click', handleClickOutside);

    // Cleanup function to remove event listeners
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showScheduleModal, showMobileMenu, showDataSelector, showOffsetEditor]);

  return (
    <div className={`ivia-timer ${isDarkMode ? 'dark-mode' : ''} ${isUIHidden ? 'ui-hidden' : ''}`}>
      {showDataSelector && (
        <div className="data-source-overlay">
          <DataSourceSelector
            onDataSourceChange={handleDataSourceChange}
            currentDataSource={dataSource}
            isLoading={isLoadingData}
            error={dataError}
            lastUpdated={lastDataUpdate}
          />
        </div>
      )}

      {showOffsetEditor && (
        <div className="offset-editor-overlay">
          <div className="offset-editor-modal">
            <h3>Edit Time Offset</h3>
            <p>Enter offset in minutes (positive = future, negative = past):</p>
            <input
              type="number"
              value={offsetInputValue}
              onChange={(e) => setOffsetInputValue(e.target.value)}
              placeholder="Enter minutes..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleOffsetSubmit();
                if (e.key === 'Escape') handleOffsetCancel();
              }}
            />
            <div className="offset-editor-buttons">
              <button onClick={handleOffsetSubmit} className="btn-primary">
                Apply
              </button>
              <button onClick={handleOffsetCancel} className="btn-secondary">
                Cancel
              </button>
            </div>
            <div className="offset-help">
              <small>Examples: 60 = +1 hour, -30 = -30 minutes, 1440 = +1 day</small>
            </div>
          </div>
        </div>
      )}

      <div className="top-bar">
        <div className="controls-section">
          <div className="control-group">
            <div
              className={`sync-status ${isRealtimeSyncConnected ? 'connected' : 'disconnected'}`}
              title={
                isRealtimeSyncConnected
                  ? `Real-time sync: ${connectedClients} clients connected`
                  : 'Real-time sync: disconnected'
              }
            >
              <FontAwesomeIcon icon={faSync} />
              {connectedClients > 1 && <span className="client-count">{connectedClients}</span>}
            </div>
          </div>

          <span className="toolbar-divider" />

          <div className="control-group">
            <button
              className="control-btn"
              onClick={toggleDarkMode}
              title="Toggle Dark Mode"
              aria-label="Toggle Dark Mode"
            >
              <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} />
            </button>
            <button className="control-btn" onClick={toggleSound} title="Toggle Sound" aria-label="Toggle Sound">
              <FontAwesomeIcon icon={isSoundEnabled ? faBell : faBellSlash} />
            </button>
          </div>

          <span className="toolbar-divider" />

          <div className="control-group desktop-only">
            <button className="control-btn" onClick={zoomIn} title="Zoom In" aria-label="Zoom In">
              <FontAwesomeIcon icon={faSearchPlus} />
            </button>
            <button className="control-btn" onClick={zoomOut} title="Zoom Out" aria-label="Zoom Out">
              <FontAwesomeIcon icon={faSearchMinus} />
            </button>
          </div>

          <span className="toolbar-divider desktop-only" />

          <div className="control-group desktop-only">
            <button
              className="control-btn"
              onClick={toggleScheduleModal}
              title="Show Schedule"
              aria-label="Show Schedule"
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
            </button>
            <button
              className={`control-btn ${showDataSelector ? 'active' : ''}`}
              onClick={() => setShowDataSelector(!showDataSelector)}
              title="Data Source Settings"
              aria-label="Data Source Settings"
            >
              <FontAwesomeIcon icon={faCog} />
            </button>
            <button
              className="control-btn"
              onClick={toggleTextSize}
              title="Toggle Text Size"
              aria-label="Toggle Text Size"
            >
              <FontAwesomeIcon icon={faTextHeight} />
            </button>
            <button
              className="control-btn"
              onClick={toggleMiniSchedule}
              title="Toggle Mini Schedule"
              aria-label="Toggle Mini Schedule"
            >
              <FontAwesomeIcon icon={showMiniSchedule ? faEyeSlash : faEye} />
            </button>
            <button
              className="control-btn"
              onClick={refreshCurrentData}
              disabled={isLoadingData}
              title="Refresh Data"
              aria-label="Refresh Data"
            >
              <FontAwesomeIcon icon={faSync} spin={isLoadingData} />
            </button>
          </div>

          <span className="toolbar-divider desktop-only" />

          <button
            className="control-btn desktop-only"
            onClick={toggleUIVisibility}
            title="Hide Controls (auto-shows on mouse move)"
            aria-label="Hide Controls"
          >
            <FontAwesomeIcon icon={faEyeSlash} />
          </button>

          <button className="control-btn mobile-menu-btn" onClick={toggleMobileMenu} aria-label="More Options">
            <FontAwesomeIcon icon={showMobileMenu ? faTimes : faBars} />
          </button>

          {showMobileMenu && (
            <div className="mobile-dropdown-menu">
              <button
                className="mobile-menu-item"
                onClick={() => {
                  zoomIn();
                  setShowMobileMenu(false);
                }}
              >
                <FontAwesomeIcon icon={faSearchPlus} />
                <span>Zoom In</span>
              </button>
              <button
                className="mobile-menu-item"
                onClick={() => {
                  zoomOut();
                  setShowMobileMenu(false);
                }}
              >
                <FontAwesomeIcon icon={faSearchMinus} />
                <span>Zoom Out</span>
              </button>
              <button
                className="mobile-menu-item"
                onClick={() => {
                  toggleScheduleModal();
                  setShowMobileMenu(false);
                }}
              >
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>Show Schedule</span>
              </button>
              <button
                className="mobile-menu-item"
                onClick={() => {
                  setShowDataSelector(!showDataSelector);
                  setShowMobileMenu(false);
                }}
              >
                <FontAwesomeIcon icon={faCog} />
                <span>Data Settings</span>
              </button>
              <button
                className="mobile-menu-item"
                onClick={() => {
                  toggleTextSize();
                  setShowMobileMenu(false);
                }}
              >
                <FontAwesomeIcon icon={faTextHeight} />
                <span>Text Size</span>
              </button>
              <button
                className="mobile-menu-item"
                onClick={() => {
                  toggleMiniSchedule();
                  setShowMobileMenu(false);
                }}
              >
                <FontAwesomeIcon icon={showMiniSchedule ? faEyeSlash : faEye} />
                <span>{showMiniSchedule ? 'Hide' : 'Show'} Mini Schedule</span>
              </button>
              <button
                className="mobile-menu-item"
                onClick={() => {
                  refreshCurrentData();
                  setShowMobileMenu(false);
                }}
                disabled={isLoadingData}
              >
                <FontAwesomeIcon icon={faSync} spin={isLoadingData} />
                <span>Refresh Data</span>
              </button>
              <button
                className="mobile-menu-item"
                onClick={() => {
                  toggleUIVisibility();
                  setShowMobileMenu(false);
                }}
              >
                <FontAwesomeIcon icon={faEyeSlash} />
                <span>Hide Controls</span>
              </button>
            </div>
          )}
        </div>

        <div className="logo-section">
          <img
            src={isDarkMode ? 'images/IVIA/logo/dark.png' : 'images/IVIA/logo/light.png'}
            alt="IVIA Logo"
            className="ivia-logo"
          />
        </div>
      </div>

      <div className="offset-controls">
        <button className="control-btn" onClick={() => adjustOffset(-1)} aria-label="Decrease Delay">
          <FontAwesomeIcon icon={faMinus} />
        </button>
        <div
          className={`offset-display ${scheduleOffset > 0 ? 'positive' : scheduleOffset < 0 ? 'negative' : ''}`}
          onClick={resetOffset}
          onContextMenu={handleOffsetRightClick}
          style={{ cursor: 'pointer' }}
          title={`Left click to reset, right click to edit. ${isRealtimeSyncConnected ? `Real-time sync: ${connectedClients} clients` : 'Real-time sync: disconnected'}`}
        >
          <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.3em' }} />
          {formatOffset(scheduleOffset)}
          {isRealtimeSyncConnected && connectedClients > 1 && (
            <span className="sync-indicator" title={`${connectedClients} clients connected`}>
              🔄 {connectedClients}
            </span>
          )}
        </div>
        <button className="control-btn" onClick={() => adjustOffset(1)} aria-label="Increase Delay">
          <FontAwesomeIcon icon={faPlus} />
        </button>

        <span className="offset-divider" />

        <button
          className={`transition-toggle ${transitionTime > 0 ? 'active' : ''}`}
          onClick={cycleTransitionTime}
          title={
            transitionTime > 0
              ? `Transition: ${transitionTime}min (${transitionTime * 30}s pres + ${transitionTime * 30}s disc). Click to cycle.`
              : 'Transition time: Off. Click to enable.'
          }
          aria-label="Cycle Transition Time"
        >
          <FontAwesomeIcon icon={faExchangeAlt} />
          <span className="transition-label">{transitionTime > 0 ? `${transitionTime}m` : 'Off'}</span>
        </button>
      </div>

      <div className="main-area">
        {showMiniSchedule && getSurroundingPresentations().length > 0 && (
          <div className="mini-schedule">
            {getSurroundingPresentations().map((item, index) => (
              <div
                key={index}
                className={`mini-schedule-item ${item.isCurrent ? 'current' : ''} ${item.isNext ? 'next' : ''}`}
              >
                <div className="mini-time">{item.time}</div>
                <div className="mini-presenter">{item.presenter}</div>
                <div className="mini-category">{item.category}</div>
                <div className="mini-duration">
                  {item.discussion
                    ? `${formatDuration(item.duration - item.discussion, false)}+${item.discussion} min`
                    : formatDuration(item.duration)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="center-content">
          {currentSchedule && countdown !== null ? (
            <>
              {(currentSchedule.discussion || isTransitionTime) && getPhaseLabel() && (
                <div className="phase-indicator">
                  <span
                    className={
                      isTransitionTime
                        ? 'transition-phase'
                        : isDiscussionTime
                          ? 'discussion-phase'
                          : 'presentation-phase'
                    }
                  >
                    {getPhaseLabel()}
                  </span>
                </div>
              )}
              <div className={`large-countdown ${getCountdownColor(countdown)}`} onClick={stopSound}>
                <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.5em', marginRight: '0.5em' }} />
                {formatCountdown(countdown)}
              </div>
              {((countdown <= 120000 && !isDiscussionTime && currentSchedule?.discussion) ||
                (countdown <= 60000 && isDiscussionTime && nextSchedule)) && (
                <div className="next-presenter-preview">
                  <div className="next-label">
                    {!isDiscussionTime && currentSchedule?.discussion ? 'Discussion Time' : `Coming up next:`}
                  </div>
                  {isDiscussionTime && countdown <= 60000 && nextSchedule && (
                    <div className="next-details">
                      <span className="next-name">
                        <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5em' }} />
                        {nextSchedule.presenter}
                      </span>
                      <span className="next-category">
                        <FontAwesomeIcon icon={faBook} style={{ marginRight: '0.5em' }} />
                        {nextSchedule.category}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}

          {currentSchedule?.category === 'Break' && countdown <= 600000 && nextSchedule && (
            <div className="next-presenter-preview">
              <div className="next-label">Coming up after {currentSchedule.presenter}:</div>
              <div className="next-details">
                <span className="next-name">
                  <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5em' }} />
                  {nextSchedule.presenter}
                </span>
                <span className="next-category">
                  <FontAwesomeIcon icon={faBook} style={{ marginRight: '0.5em' }} />
                  {nextSchedule.category}
                </span>
              </div>
            </div>
          )}

          <div className="timer-content">
            <h1 className={currentSchedule ? 'normal-time' : 'large-time'} onClick={stopSound}>
              {currentTime.toLocaleTimeString()}
            </h1>
            {!currentSchedule && timeUntilNextSession !== null && (
              <div className="next-session-countdown">{formatTimeUntilNextSession(timeUntilNextSession)}</div>
            )}
            {currentSchedule ? (
              <div className="schedule-info">
                <h2>
                  {typeof getSessionIcons(currentSchedule).presenterIcon === 'string' ? (
                    <span className="emoji-icon">{getSessionIcons(currentSchedule).presenterIcon}</span>
                  ) : (
                    <FontAwesomeIcon
                      icon={getSessionIcons(currentSchedule).presenterIcon}
                      style={{ marginRight: '0.5em' }}
                    />
                  )}
                  {currentSchedule.presenter}
                </h2>
                <h3>
                  {typeof getSessionIcons(currentSchedule).categoryIcon === 'string' ? (
                    <span className="emoji-icon">{getSessionIcons(currentSchedule).categoryIcon}</span>
                  ) : (
                    <FontAwesomeIcon
                      icon={getSessionIcons(currentSchedule).categoryIcon}
                      style={{ marginRight: '0.5em' }}
                    />
                  )}
                  {currentSchedule.category}
                </h3>
              </div>
            ) : (
              <div className="schedule-info">
                {getLastSession(currentTime, schedule, -1) && (
                  <div className="last-session">
                    <h2>Last Session:</h2>
                    <h3>
                      {typeof getSessionIcons(getLastSession(currentTime, schedule, -1)).presenterIcon === 'string' ? (
                        <span className="emoji-icon">
                          {getSessionIcons(getLastSession(currentTime, schedule, -1)).presenterIcon}
                        </span>
                      ) : (
                        <FontAwesomeIcon
                          icon={getSessionIcons(getLastSession(currentTime, schedule, -1)).presenterIcon}
                          style={{ marginRight: '0.5em' }}
                        />
                      )}
                      {getLastSession(currentTime, schedule, -1).presenter}
                    </h3>
                    <h4>
                      {typeof getSessionIcons(getLastSession(currentTime, schedule, -1)).categoryIcon === 'string' ? (
                        <span className="emoji-icon">
                          {getSessionIcons(getLastSession(currentTime, schedule, -1)).categoryIcon}
                        </span>
                      ) : (
                        <FontAwesomeIcon
                          icon={getSessionIcons(getLastSession(currentTime, schedule, -1)).categoryIcon}
                          style={{ marginRight: '0.5em' }}
                        />
                      )}
                      {getLastSession(currentTime, schedule, -1).category}
                    </h4>
                  </div>
                )}
                <div className="next-session">
                  {nextSchedule ? (
                    <>
                      <h2>Next Up:</h2>
                      <h3>
                        {typeof getSessionIcons(nextSchedule).presenterIcon === 'string' ? (
                          <span className="emoji-icon">{getSessionIcons(nextSchedule).presenterIcon}</span>
                        ) : (
                          <FontAwesomeIcon
                            icon={getSessionIcons(nextSchedule).presenterIcon}
                            style={{ marginRight: '0.5em' }}
                          />
                        )}
                        {nextSchedule.presenter}
                      </h3>
                      <h4>
                        {typeof getSessionIcons(nextSchedule).categoryIcon === 'string' ? (
                          <span className="emoji-icon">{getSessionIcons(nextSchedule).categoryIcon}</span>
                        ) : (
                          <FontAwesomeIcon
                            icon={getSessionIcons(nextSchedule).categoryIcon}
                            style={{ marginRight: '0.5em' }}
                          />
                        )}
                        {nextSchedule.category}
                      </h4>
                      <div className="next-time">at {nextSchedule.time}</div>
                    </>
                  ) : (
                    <div className="no-next-session">
                      <h2>No More Sessions Scheduled</h2>
                      <h4>End of Day</h4>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showScheduleModal && (
        <ScheduleModal
          showScheduleModal={showScheduleModal}
          toggleScheduleModal={toggleScheduleModal}
          schedule={schedule}
          currentSchedule={currentSchedule}
          getAdjustedTime={getAdjustedTime}
          getSessionIcons={getSessionIcons}
          formatDuration={formatDuration}
        />
      )}

      <button className="full-screen-toggle" onClick={toggleFullScreen} aria-label="Toggle Full Screen">
        <FontAwesomeIcon icon={document.fullscreenElement ? faCompress : faExpand} />
      </button>
    </div>
  );
};

export default IVIATimer;
